import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
// Role-based redirect for /dashboard: students go to student dashboard
const DashboardRedirect = () => {
  const { userProfile } = useAuth();
  if (userProfile?.role === 'user') {
    return <Navigate to="/the-stage-student-dashboard" replace />;
  }
  return <AdminHomeDashboard />;
};
// Import pages
import NotFound from "pages/NotFound";
import LoginPage from "pages/auth/LoginPage";
import RegisterPage from "pages/auth/RegisterPage";
import ForgotPasswordPage from "pages/auth/ForgotPasswordPage";
import AdminPage from "pages/admin/AdminPage";
import AdminHomeDashboard from "pages/admin-home-dashboard";
import SongManagementPage from "pages/song-management-page";
import SongDatabaseList from './pages/song-database-list';
import SongAdditionForm from './pages/song-addition-form';
import SongDetailPage from './pages/song-detail-page';
import MyAccountPage from './pages/MyAccountPage';
import TalentManagementDashboard from './pages/talent-management-dashboard';
import AddEditTalentProfile from './pages/add-edit-talent-profile';
import VideoManagementDashboard from './pages/video-management-dashboard';
import AllVideosManagement from './pages/all-videos-management';
import TalentDetailPage from './pages/talent-detail-page';
import SettingsManagement from './pages/settings-management';
import AddMusical from './pages/add-musical';
import AllMusicals from './pages/all-musicals';
import AllRoles from './pages/all-roles';
import RoleDetailPage from './pages/role-detail-page';
import AllBadges from './pages/all-badges';
import BadgeDetailPage from './pages/badge-detail-page';
import AddBadge from './pages/add-badge';
import ToolsAdminPage from './pages/tools-admin-page';
import HiringPage from './pages/hiring-page';
// Import policy pages
import PrivacyPolicyPage from './pages/privacy-policy-page';
import TermsOfServicePage from './pages/terms-of-service-page';
import SupportPage from './pages/support-page';
// Import promotional website
import PromotionalWebsite from './pages/promotional-website';
// Import student pages
import TheStageStudentDashboard from './pages/the-stage-student-dashboard';
import MusicalProgressPage from './pages/musical-progress-page';
import SongProgressPage from './pages/song-progress-page';
import StudentMusicalsDiscovery from './pages/student-musicals-discovery';
import StudentSongsDiscovery from './pages/student-songs-discovery';
import StudentRolesDiscovery from './pages/student-roles-discovery';
import StudentNotificationCenter from './pages/student-notification-center';
import NotificationAnalyticsDashboard from "./pages/notification-analytics-dashboard";
import RecommendationPerformanceAnalyticsDashboard from "./pages/recommendation-performance-analytics-dashboard";
import StudentPersonalAnalyticsDashboard from './pages/student-personal-analytics-dashboard';
import RealTimeActivityFeedDashboard from "./pages/real-time-activity-feed-dashboard";
import CommunityLeaderboardsDashboard from './pages/community-leaderboards-dashboard';
// Import Pitch Training Tools
import StudentPitchCoachTool from './pages/student-pitch-coach-tool';
import AdminMidiUploadTool from './pages/admin-midi-upload-tool';
// Student Audition Songs Tool
import StudentAuditionSongsTool from './pages/student-audition-songs-tool';
// Breath & Timing Coach Tool
import BreathTimingCoachTool from './pages/breath-timing-coach-tool';
// Import Custom Practice Session Builder
import CustomPracticeSessionBuilder from './pages/custom-practice-session-builder';
import ActivePracticeSessionInterface from './pages/active-practice-session-interface';
import PracticeSessionAnalyticsDashboard from './pages/practice-session-analytics-dashboard';
import StudentProfileEditor from './pages/student-profile-editor';
import EducatorProfileManagement from './pages/educator-profile-management';
import UniversalAccountSettingsHub from './pages/universal-account-settings-hub';
import LessonLearningInterface from './pages/lesson-learning-interface';
import VideoEngagementAnalyticsDashboard from './pages/video-engagement-analytics-dashboard';
import VideoPlaybackAnalytics from './pages/video-playback-analytics';
import WarmUpsManagement from './pages/warm-ups-management';
import WarmUpsBrowse from './pages/warm-ups-browse';
import WarmUpDetail from './pages/warm-up-detail';

export default function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Root path - Promotional Website (Public Homepage) */}
          <Route path="/" element={<PromotionalWebsite />} />
          {/* Auth routes */}
          <Route path="/login-page" element={<LoginPage />} />
          <Route path="/register-page" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* Auth redirects - fix 404 errors by redirecting to actual auth pages */}
          <Route path="/auth/login" element={<Navigate to="/login-page" replace />} />
          <Route path="/auth/register" element={<Navigate to="/register-page" replace />} />
          <Route path="/auth/forgot-password" element={<Navigate to="/forgot-password" replace />} />
          {/* Policy pages - Public */}
          <Route path="/privacy-policy-page" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service-page" element={<TermsOfServicePage />} />
          <Route path="/support-page" element={<SupportPage />} />
          {/* Public pages */}
          <Route path="/hiring-page" element={<HiringPage />} />
          {/* Talent Detail - Public (for promotional website) */}
          <Route path="/talent-detail/:talentId" element={<TalentDetailPage />} />
          {/* Dashboard - Role-based routing */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            }
          />
          {/* Redirect /admin-home-dashboard to /dashboard */}
          <Route path="/admin-home-dashboard" element={<Navigate to="/dashboard" replace />} />
          {/* Redirect old student-progress-dashboard to the-stage-student-dashboard */}
          <Route 
            path="/student-progress-dashboard" 
            element={<Navigate to="/the-stage-student-dashboard" replace />}
          />
          {/* Student Ecosystem Routes - UPDATED */}
          <Route
            path="/the-stage-student-dashboard"
            element={
              <ProtectedRoute>
                <TheStageStudentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-notification-center"
            element={
              <ProtectedRoute>
                <StudentNotificationCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notification-analytics-dashboard"
            element={
              <ProtectedRoute requiredRole="superuser">
                <NotificationAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recommendation-performance-analytics-dashboard"
            element={
              <ProtectedRoute requiredRole="superuser">
                <RecommendationPerformanceAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video-engagement-analytics-dashboard"
            element={
              <ProtectedRoute requiredRole="superuser">
                <VideoEngagementAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video-playback-analytics"
            element={
              <ProtectedRoute requiredRole="superuser">
                <VideoPlaybackAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-musicals-discovery"
            element={
              <ProtectedRoute>
                <StudentMusicalsDiscovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-songs-discovery"
            element={
              <ProtectedRoute>
                <StudentSongsDiscovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-roles-discovery"
            element={
              <ProtectedRoute>
                <StudentRolesDiscovery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/musical-progress-page"
            element={
              <ProtectedRoute>
                <MusicalProgressPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/song-progress-page"
            element={
              <ProtectedRoute>
                <SongProgressPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/song-progress-page/:id"
            element={
              <ProtectedRoute>
                <SongProgressPage />
              </ProtectedRoute>
            }
          />
          {/* Protected routes - Editor level MINIMUM (song database list is now editor-only) */}
          <Route 
            path="/song-database-list" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongDatabaseList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/song-detail-page" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/song-detail/:id" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongDetailPage />
              </ProtectedRoute>
            } 
          />
          {/* My Account - Available to all authenticated users */}
          <Route 
            path="/my-account" 
            element={
              <ProtectedRoute>
                <MyAccountPage />
              </ProtectedRoute>
            } 
          />
          {/* Musicals and Roles - Editor level MINIMUM */}
          <Route 
            path="/all-musicals" 
            element={
              <ProtectedRoute requiredRole="editor">
                <AllMusicals />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/all-roles" 
            element={
              <ProtectedRoute requiredRole="editor">
                <AllRoles />
              </ProtectedRoute>
            } 
          />
          {/* Role Detail Page - Primary route using query parameters */}
          <Route 
            path="/role-detail-page" 
            element={
              <ProtectedRoute requiredRole="editor">
                <RoleDetailPage />
              </ProtectedRoute>
            } 
          />
          {/* Role Detail Page - URL parameter support (secondary) */}
          <Route 
            path="/role-detail-page/:id" 
            element={
              <ProtectedRoute requiredRole="editor">
                <RoleDetailPage />
              </ProtectedRoute>
            } 
          />
          {/* Protected routes - Editor level (can create/edit songs and musicals) */}
          <Route 
            path="/song-addition-form" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongAdditionForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/song-addition-form/:id" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongAdditionForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-musical" 
            element={
              <ProtectedRoute requiredRole="editor">
                <AddMusical />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-musical/:id" 
            element={
              <ProtectedRoute requiredRole="editor">
                <AddMusical />
              </ProtectedRoute>
            } 
          />
          {/* Redirect /admin/musicals routes to correct paths */}
          <Route 
            path="/admin/musicals" 
            element={<Navigate to="/all-musicals" replace />}
          />
          <Route 
            path="/admin/musicals/edit/:id" 
            element={<Navigate to="/add-musical/:id" replace />}
          />
          {/* Backward compatibility routes - redirect old paths to new ones */}
          <Route 
            path="/song-analysis-form" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongAdditionForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/song-analysis-form/:id" 
            element={
              <ProtectedRoute requiredRole="editor">
                <SongAdditionForm />
              </ProtectedRoute>
            } 
          />
          {/* Protected routes - SuperUser level (admin) */}
          <Route 
            path="/tools-admin-page" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <ToolsAdminPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AdminPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-dashboard" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AdminPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/song-management-page" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <SongManagementPage />
              </ProtectedRoute>
            } 
          />
          {/* Talent Management - SuperUser only */}
          <Route 
            path="/talent-management-dashboard" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <TalentManagementDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-edit-talent-profile" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AddEditTalentProfile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-edit-talent-profile/:id" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AddEditTalentProfile />
              </ProtectedRoute>
            } 
          />
          {/* Video Management - SuperUser only */}
          <Route 
            path="/video-management-dashboard" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <VideoManagementDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/all-videos-management" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AllVideosManagement />
              </ProtectedRoute>
            } 
          />
          {/* Badge Management - SuperUser only */}
          <Route 
            path="/all-badges" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AllBadges />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-badge" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AddBadge />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/badge-detail-page" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <BadgeDetailPage />
              </ProtectedRoute>
            } 
          />
          {/* Settings Management - SuperUser only */}
          <Route 
            path="/settings-management" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <SettingsManagement />
              </ProtectedRoute>
            } 
          />
          {/* Community Leaderboards Dashboard */}
          <Route 
            path="/community-leaderboards-dashboard" 
            element={
              <ProtectedRoute requireAuth={true}>
                <CommunityLeaderboardsDashboard />
              </ProtectedRoute>
            } 
          />
          {/* Student Personal Analytics Dashboard */}
          <Route 
            path="/student-personal-analytics-dashboard" 
            element={
              <ProtectedRoute>
                <StudentPersonalAnalyticsDashboard />
              </ProtectedRoute>
            } 
          />
          {/* Student Pitch Coach Tool - Available to all students */}
          <Route 
            path="/student-pitch-coach-tool" 
            element={
              <ProtectedRoute>
                <StudentPitchCoachTool />
              </ProtectedRoute>
            } 
          />
          {/* Student Audition Songs Tool - Protected Route */}
          <Route
            path="/student-audition-songs-tool"
            element={
              <ProtectedRoute allowedRoles={['user']}>
                <StudentAuditionSongsTool />
              </ProtectedRoute>
            }
          />
          {/* Breath & Timing Coach Tool - Available to all students */}
          <Route 
            path="/breath-timing-coach-tool" 
            element={
              <ProtectedRoute>
                <BreathTimingCoachTool />
              </ProtectedRoute>
            } 
          />
          {/* Custom Practice Session Builder - Available to all students */}
          <Route 
            path="/custom-practice-session-builder" 
            element={
              <ProtectedRoute>
                <CustomPracticeSessionBuilder />
              </ProtectedRoute>
            } 
          />
          {/* Active Practice Session Interface - Available to all students */}
          <Route 
            path="/active-practice-session-interface" 
            element={
              <ProtectedRoute>
                <ActivePracticeSessionInterface />
              </ProtectedRoute>
            } 
          />
          {/* Admin MIDI Upload Tool - SuperUser only */}
          <Route 
            path="/admin-midi-upload-tool" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <AdminMidiUploadTool />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/practice-session-analytics-dashboard"
            element={
              <ProtectedRoute>
                <PracticeSessionAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          {/* Student Profile Editor */}
          <Route
            path="/student-profile-editor"
            element={
              <ProtectedRoute>
                <StudentProfileEditor />
              </ProtectedRoute>
            }
          />
          {/* Educator Profile Management */}
          <Route
            path="/educator-profile-management"
            element={
              <ProtectedRoute>
                <EducatorProfileManagement />
              </ProtectedRoute>
            }
          />
          {/* Universal Account Settings Hub */}
          <Route
            path="/universal-account-settings-hub"
            element={
              <ProtectedRoute>
                <UniversalAccountSettingsHub />
              </ProtectedRoute>
            }
          />
          {/* Lesson Learning Interface */}
          <Route
            path="/lesson-interface"
            element={
              <ProtectedRoute>
                <LessonLearningInterface />
              </ProtectedRoute>
            }
          />
          {/* Warm Ups — Admin Management */}
          <Route
            path="/warm-ups-management"
            element={
              <ProtectedRoute requiredRole="superuser">
                <WarmUpsManagement />
              </ProtectedRoute>
            }
          />
          {/* Warm Ups — Student Browse */}
          <Route
            path="/warm-ups"
            element={
              <ProtectedRoute>
                <WarmUpsBrowse />
              </ProtectedRoute>
            }
          />
          {/* Warm Up Detail */}
          <Route
            path="/warm-ups/:id"
            element={
              <ProtectedRoute>
                <WarmUpDetail />
              </ProtectedRoute>
            }
          />
          {/* Fallback - 404 Not Found */}
          <Route path="*" element={<NotFound />} />
          <Route 
            path="/real-time-activity-feed-dashboard" 
            element={
              <ProtectedRoute requiredRole="superuser">
                <RealTimeActivityFeedDashboard />
              </ProtectedRoute>
            } 
          />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}